import { useCallback } from 'react'

import { Link, useNavigate } from 'react-router-dom'

import { Post } from '@tribeplatform/gql-client/types'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useDeletePost, usePublishPost } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { confirm } from '@tribeplatform/react-ui-kit/Dialog'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

interface Props {
  post: Post
}

export const PostActionButtons = ({ post }: Props) => {
  const { mutateAsync: publishPost, isLoading: isPublishing } = usePublishPost()
  const { mutateAsync: deletePost, isLoading: isDeleting } = useDeletePost()
  const { $t } = useI18n()
  const navigate = useNavigate()

  const handlePublishPost = useCallback(async () => {
    const confirmed = await confirm({
      title: $t({
        id: 'Moderation.Post.PublishConfirmation',
        defaultMessage: 'Are you sure you want to publish this post?',
      }),
    })

    if (!confirmed) {
      return
    }

    try {
      await publishPost({ postId: post.id })
      navigate(post.relativeUrl)
    } catch (error) {
      toast({
        title: $t({
          id: 'Generics.Error',
          defaultMessage: 'Error',
        }),
        description: $t({
          id: 'Moderation.Post.PublishError',
          defaultMessage: 'Failed to publish the post',
        }),
      })
    }
  }, [publishPost, post.id, post.relativeUrl, navigate, $t])

  const handleDeletePost = useCallback(async () => {
    const confirmed = await confirm({
      title: $t({
        id: 'Moderation.Post.DeleteConfirmation',
        defaultMessage: 'Are you sure you want to delete this post?',
      }),
      description: $t({
        id: 'Moderation.Post.DeleteConfirmationDescription',
        defaultMessage: 'This action cannot be undone.',
      }),
      danger: true,
    })

    if (!confirmed) {
      return
    }

    try {
      await deletePost({ id: post.id })
    } catch (error) {
      toast({
        title: $t({
          id: 'Generics.Error',
          defaultMessage: 'Error',
        }),
        description: $t({
          id: 'Moderation.Post.DeleteError',
          defaultMessage: 'Failed to delete the post',
        }),
      })
    }
  }, [post.id, deletePost, $t])

  return (
    <div className="flex space-s-3">
      <Button
        className="flex-1 justify-center"
        loading={isPublishing}
        disabled={isPublishing}
        variant="primary"
        onClick={handlePublishPost}
      >
        <T
          defaultMessage="Publish now"
          id="Moderation.Post.Drafts.PublishNow"
        />
      </Button>
      <Button
        as={Link}
        to={`${post?.relativeUrl}/edit`}
        className="flex-1 justify-center"
        variant="secondaryNeutral"
      >
        <T defaultMessage="Edit" id="Generics.Edit" />
      </Button>
      <Button
        className="flex-1 justify-center"
        loading={isDeleting}
        disabled={isDeleting}
        onClick={handleDeletePost}
        variant="secondaryNeutral"
      >
        <T defaultMessage="Delete" id="Generics.Delete" />
      </Button>
    </div>
  )
}
